import { useEffect, useState } from "react";
import { loadSiteInstructions, saveSiteInstructions, type SiteDefaults } from "../lib/siteData";
import { showAlert } from "../lib/dialog";

const EMPTY_DEFAULTS: SiteDefaults = { postCategory: "", postTags: "", pageTags: "", saParent: "", saTags: "", tone: "" };

// The Default SA Parent field takes a path (no domain). If a full URL is pasted, keep only the
// path portion (e.g. "https://site.com/locations/" -> "/locations/"); a bare path is left as-is.
function stripToPath(v: string): string {
  const s = v.trim();
  if (!s) return "";
  const firstSeg = s.replace(/^https?:\/\//i, "").split("/")[0];
  const looksLikeDomain = /^[^/\s]+\.[a-z]{2,}(:\d+)?$/i.test(firstSeg);
  if (/^https?:\/\//i.test(s) || looksLikeDomain) {
    try { const u = new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`); return u.pathname + u.search + u.hash; } catch { /* fall through */ }
  }
  return s;
}

interface Props {
  open: boolean;
  siteKey: string;
  label: string;
  onClose: () => void;
}

// Per-site Global Custom Instructions editor. When enabled with text, these are prefilled into
// the Custom Instructions box of every New Page creation form for this site (the user can still
// edit/remove them per page). Stored in the site's audit blob via siteData helpers.
export function GlobalInstructionsDialog({ open, siteKey, label, onClose }: Props) {
  const [text, setText] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [defaults, setDefaults] = useState<SiteDefaults>(EMPTY_DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    loadSiteInstructions(siteKey)
      .then(r => { if (alive) { setText(r.text); setEnabled(r.enabled); setDefaults(r.defaults ?? EMPTY_DEFAULTS); } })
      .catch(() => { if (alive) { setText(""); setEnabled(false); setDefaults(EMPTY_DEFAULTS); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, siteKey]);

  const setDef = (k: keyof SiteDefaults) => (e: React.ChangeEvent<HTMLInputElement>) => setDefaults(d => ({ ...d, [k]: e.target.value }));

  if (!open) return null;

  async function handleSave() {
    setSaving(true);
    try {
      await saveSiteInstructions(siteKey, text.trim(), enabled, label, { ...defaults, saParent: stripToPath(defaults.saParent) });
      onClose();
    } catch (e) {
      void showAlert(`Couldn't save the instructions: ${e instanceof Error ? e.message : "Unknown error"}`, { title: "Save failed", tone: "danger" });
    } finally {
      setSaving(false);
    }
  }

  const btn: React.CSSProperties = { padding: "8px 18px", border: "none", borderRadius: 0, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter','Mulish',sans-serif" };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 4200, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "8vh 16px" }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 12, maxWidth: 600, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,.35)", padding: "22px 24px", color: "#334155", lineHeight: 1.5 }}
      >
        <h2 style={{ margin: "0 0 4px", fontSize: 17, color: "#0f172a", fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.7px" }}>
          Global Instructions
        </h2>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12, wordBreak: "break-word" }}>{label || siteKey}</div>

        <p style={{ fontSize: 12.5, color: "#475569", margin: "0 0 12px", lineHeight: 1.55 }}>
          Site-wide instructions for new pages. When <strong>enabled</strong>, these are prefilled
          into the <strong>Custom Instructions</strong> box of every New Page creation for this
          website — you can still edit or remove them on any individual page.
        </p>

        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          disabled={loading || saving}
          rows={7}
          placeholder={loading ? "Loading…" : "e.g. Always use a friendly, professional tone. Mention our 24/7 admissions line. Link to /insurance/ where relevant."}
          style={{ width: "100%", boxSizing: "border-box", resize: "vertical", padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 13, fontFamily: "'Inter','Mulish',sans-serif", lineHeight: 1.5, color: "#0f172a" }}
        />

        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "12px 0 18px", fontSize: 12.5, color: "#334155", cursor: loading || saving ? "default" : "pointer" }}>
          <input type="checkbox" checked={enabled} disabled={loading || saving} onChange={e => setEnabled(e.target.checked)} style={{ width: 15, height: 15, cursor: "inherit" }} />
          <span><strong>Enable</strong> — prefill these into every new page's Custom Instructions for this site</span>
        </label>

        {/* Content tone / voice — used for all new page content generated for this site */}
        <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 16, marginBottom: 8 }}>
          <label style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>Content Tone / Voice <span style={{ fontWeight: 400, color: "#64748b" }}>(optional)</span></label>
          <textarea
            value={defaults.tone}
            onChange={e => setDefaults(d => ({ ...d, tone: e.target.value }))}
            disabled={loading || saving}
            rows={5}
            placeholder={loading ? "Loading…" : "Describe the desired tone (e.g. warm, reassuring, plain-spoken; avoid hype) — or paste a sample of the site's existing copy to emulate its voice. Used for all new page content generated for this website."}
            style={{ width: "100%", boxSizing: "border-box", resize: "vertical", padding: "8px 11px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, fontFamily: "'Inter','Mulish',sans-serif", lineHeight: 1.5, color: "#0f172a", outline: "none" }}
          />
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Applied to the content generated for every new page on this website.</div>
        </div>

        {/* Optional per-site defaults — prefill the matching New Page Details / Service Area fields */}
        <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 16, marginBottom: 8 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a", marginBottom: 2 }}>Default Field Values <span style={{ fontWeight: 400, color: "#64748b" }}>(optional)</span></div>
          <p style={{ fontSize: 11.5, color: "#64748b", margin: "0 0 12px", lineHeight: 1.5 }}>
            When set, these prefill the matching fields on every new page created for this site — you can still change them per page.
          </p>
          {([
            { k: "postCategory" as const, label: "Default Post Category", ph: "e.g. News" },
            { k: "postTags" as const, label: "Default Post Tags", ph: "e.g. Article, Updates" },
            { k: "pageTags" as const, label: "Default Page Tags", ph: "e.g. Service" },
            { k: "saParent" as const, label: "Default Service Area / Location Page Parent Page", ph: "e.g. /locations/ (path only — domain is removed)" },
            { k: "saTags" as const, label: "Default Service Area / Location Page Tags", ph: "e.g. Service Area, Location Page" },
          ]).map(f => (
            <div key={f.k} style={{ marginBottom: 12 }}>
              <label style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase", color: "#64748b", marginBottom: 4 }}>{f.label}</label>
              <input
                value={defaults[f.k]}
                onChange={setDef(f.k)}
                onBlur={f.k === "saParent" ? e => { const p = stripToPath(e.target.value); if (p !== defaults.saParent) setDefaults(d => ({ ...d, saParent: p })); } : undefined}
                disabled={loading || saving}
                placeholder={loading ? "Loading…" : f.ph}
                style={{ width: "100%", boxSizing: "border-box", padding: "8px 11px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 13, fontFamily: "'Inter','Mulish',sans-serif", color: "#0f172a", outline: "none" }}
              />
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button style={{ ...btn, background: "#e2e8f0", color: "#334155" }} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{ ...btn, background: "#16a34a", color: "#fff", opacity: loading || saving ? 0.7 : 1 }} onClick={handleSave} disabled={loading || saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
